import * as React from "react"
import { Menu, PanelLeftClose, PanelLeftOpen } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./Button"

interface SidebarContextValue {
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
}

const SidebarContext = React.createContext<SidebarContextValue | undefined>(undefined)

export function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider")
  }
  return context
}

interface SidebarProviderProps {
  children: React.ReactNode
  defaultCollapsed?: boolean
}

export function SidebarProvider({ children, defaultCollapsed = false }: SidebarProviderProps) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed)
  
  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      {children}
    </SidebarContext.Provider>
  )
}

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export function Sidebar({ children, className, ...props }: SidebarProps) {
  const { collapsed } = useSidebar()

  return (
    <div
      className={cn(
        "relative flex h-full flex-col border-r bg-background transition-all duration-300",
        collapsed ? "w-16" : "w-64",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function SidebarHeader({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("h-12 border-b", className)} {...props}>
      {children}
    </div>
  )
}

export function SidebarContent({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex-1 overflow-hidden", className)} {...props}>
      <div className="h-full overflow-y-auto px-2 py-2">
        {children}
      </div>
    </div>
  )
}

export function SidebarFooter({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("border-t p-2", className)} {...props}>
      {children}
    </div>
  )
}

interface SidebarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: string
}

export function SidebarGroup({ children, label, className, ...props }: SidebarGroupProps) {
  const { collapsed } = useSidebar()

  return (
    <div className={cn("mb-4", className)} {...props}>
      {label && !collapsed && (
        <h3 className="mb-2 px-2 text-11 font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </h3>
      )}
      <div className="space-y-0.5">
        {children}
      </div>
    </div>
  )
}

interface SidebarItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  icon?: React.ReactNode
  badge?: React.ReactNode
  active?: boolean
}

export function SidebarItem({
  children,
  icon,
  badge,
  active,
  className,
  ...props
}: SidebarItemProps) {
  const { collapsed } = useSidebar()

  return (
    <a
      className={cn(
        "relative flex items-center gap-2 rounded-md px-2 py-1.5 text-13 transition-all",
        active
          ? "bg-accent/30 text-accent-foreground font-medium before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-0.5 before:rounded-r-full before:bg-primary"
          : "text-muted-foreground font-normal hover:bg-accent/20 hover:text-accent-foreground",
        collapsed && "justify-center px-2",
        className
      )}
      {...props}
    >
      {icon && (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          {icon}
        </span>
      )}
      {!collapsed && (
        <>
          <span className="flex-1">{children}</span>
          {badge}
        </>
      )}
    </a>
  )
}

export function SidebarTrigger({ className, onClick, color, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { color?: any }) {
  const { collapsed, setCollapsed } = useSidebar()
  
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn("h-8 w-8", className)}
      onClick={(e) => {
        setCollapsed(!collapsed)
        onClick?.(e)
      }}
      {...props}
    >
      {collapsed ? (
        <PanelLeftOpen className="h-4 w-4" />
      ) : (
        <PanelLeftClose className="h-4 w-4" />
      )}
    </Button>
  )
}

export function SidebarMobileTrigger({ className, color, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { color?: any }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn("md:hidden", className)}
      {...props}
    >
      <Menu className="h-5 w-5" />
    </Button>
  )
}